import { cn } from "@/lib/utils";
import { formatMoney, parsePrice } from "@shared/currency";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { GRADING_TOOLTIP, defaultGradeDetailFor, normalizeGradeLabel, type ResolvedGradingCopy } from "@shared/grading";
import { Link } from "wouter";
import { Boxes, Info } from "lucide-react";

/**
 * Per-category grading copy + pallet-pill metadata (Task #156).
 *
 * `gradingResolved` is built server-side (see shared/grading.resolveGradingCopy)
 * so the refrigerator defaults live in one place, not in this rendering
 * component. When `gradingResolved` is omitted (legacy callers, or while
 * the categories list is still loading) the badge falls back to the
 * shared GRADING_TOOLTIP + defaultGradeDetailFor so the existing
 * refrigerator UX is unchanged.
 *
 * For categories whose `saleType === "pallets"`, the colored letter
 * badge is replaced with a neutral "[icon] {pillLabel} · $price" pill
 * so the standalone red "X" no longer reads as a close button on
 * Shark/Ninja and microwave pallet cards.
 */
export interface GradeBadgeCategory {
  name?: string | null;
  iconUrl?: string | null;
  saleType?: "products" | "pallets" | null;
  gradingResolved?: ResolvedGradingCopy;
}

interface GradeBadgeProps {
  grade: string;
  className?: string;
  price?: number;
  compact?: boolean;
  /** When false, render as a plain badge without the explanatory tooltip wrapper */
  withTooltip?: boolean;
  /** Optional per-category copy + pallet-pill metadata (Task #156). */
  category?: GradeBadgeCategory | null;
}

function getGradeColor(grade: string) {
  const normalizedGrade = grade.toUpperCase().trim();

  switch (normalizedGrade) {
    case 'A-':
    case 'CLASS A MINUS':
      return 'bg-blue-100 text-blue-800 border-blue-400';

    case 'A':
    case 'CLASS A':
      return 'bg-green-100 text-green-800 border-green-300';

    case 'B+':
    case 'CLASS B PLUS':
    case 'B-COMPLETE':
      return 'bg-orange-100 text-orange-800 border-orange-300';

    case 'B':
    case 'B-':
    case 'CLASS B MINUS':
    case 'B-INCOMPLETE':
      return 'bg-yellow-100 text-yellow-800 border-yellow-300';

    case 'C':
    case 'H':
      return 'bg-orange-100 text-orange-800 border-orange-300';

    case 'X':
    case 'VX':
      return 'bg-red-100 text-red-800 border-red-300';

    case 'Z':
    case 'ZH':
      return 'bg-purple-100 text-purple-800 border-purple-300';

    default:
      return 'bg-gray-100 text-gray-800 border-gray-300';
  }
}

/** Resolve the per-grade detail line, preferring server-resolved admin copy. */
function resolveDetail(grade: string, category?: GradeBadgeCategory | null): string | null {
  const detail = category?.gradingResolved?.detail;
  if (detail) {
    const normalized = grade.toUpperCase().trim();
    const map: Record<string, keyof typeof detail> = {
      'A-': 'A-',
      'CLASS A MINUS': 'A-',
      'A': 'A',
      'CLASS A': 'A',
      'B+': 'B+',
      'CLASS B PLUS': 'B+',
      'B-COMPLETE': 'B+',
      'B': 'B-',
      'B-': 'B-',
      'CLASS B MINUS': 'B-',
      'B-INCOMPLETE': 'B-',
    };
    const key = map[normalized];
    if (key) {
      const v = detail[key];
      if (typeof v === 'string' && v.trim().length > 0) return v;
    }
  }
  return defaultGradeDetailFor(grade);
}

export function GradeBadge({
  grade,
  className,
  price,
  compact = false,
  withTooltip = true,
  category,
}: GradeBadgeProps) {
  const isPalletCategory = category?.saleType === "pallets";

  const sizeClasses = compact
    ? 'px-1.5 py-0.5 text-[10px] sm:text-xs sm:px-2'
    : 'px-2.5 py-0.5 text-[16px]';

  const tooltipBlurb = category?.gradingResolved?.tooltip ?? GRADING_TOOLTIP;

  // Pallet category: render a neutral category-icon + label pill instead
  // of the colored letter badge (Task #156).
  if (isPalletCategory) {
    const label = category?.gradingResolved?.pillLabel ?? "Pallet";
    const iconSize = compact ? "h-3 w-3" : "h-3.5 w-3.5";
    const pill = (
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-full font-semibold border bg-slate-100 text-slate-800 border-slate-300 dark:bg-slate-800 dark:text-slate-100 dark:border-slate-700",
          sizeClasses,
          withTooltip && "cursor-help",
          className,
        )}
        // Preserve the existing test-id contract so e2e selectors that
        // target `grade-badge-${grade}` keep working on pallet rows; add
        // a descriptive supplementary id for new tests.
        data-testid={`grade-badge-${grade}`}
        data-pill="pallet"
      >
        {category?.iconUrl ? (
          <img
            src={category.iconUrl}
            alt=""
            className={cn(iconSize, "object-contain")}
          />
        ) : (
          <Boxes className={iconSize} aria-hidden="true" />
        )}
        <span>{label}</span>
        {price !== undefined && (
          <span className={compact ? "ml-0.5" : "ml-1"}>{formatMoney(parsePrice(price))}</span>
        )}
        {withTooltip && (
          <Info
            aria-hidden="true"
            className={cn("ml-0.5 opacity-70", compact ? "h-2.5 w-2.5 sm:h-3 sm:w-3" : "h-3 w-3")}
            data-testid={`grade-info-icon-${grade}`}
          />
        )}
      </span>
    );

    if (!withTooltip) return pill;

    return (
      <Tooltip>
        <TooltipTrigger asChild>{pill}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs leading-snug">
          <p>{tooltipBlurb}</p>
          <p className="mt-1 text-muted-foreground">
            See the{' '}
            <Link href="/grading" className="underline">
              grading guide
            </Link>
            .
          </p>
        </TooltipContent>
      </Tooltip>
    );
  }

  const badge = (
    <span
      className={cn(
        "inline-flex items-center rounded-full font-semibold border",
        sizeClasses,
        getGradeColor(grade),
        withTooltip && "cursor-help",
        className
      )}
      data-testid={`grade-badge-${grade}`}
    >
      {normalizeGradeLabel(grade)}
      {price !== undefined && (
        <span className={compact ? "ml-0.5 sm:ml-1" : "ml-1"}>{formatMoney(parsePrice(price))}</span>
      )}
      {withTooltip && (
        <Info
          aria-hidden="true"
          className={cn(
            "ml-1 opacity-70",
            compact ? "h-2.5 w-2.5 sm:h-3 sm:w-3" : "h-3 w-3"
          )}
          data-testid={`grade-info-icon-${grade}`}
        />
      )}
    </span>
  );

  if (!withTooltip) return badge;

  const detail = resolveDetail(grade, category);

  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs leading-snug">
        <p>{detail ?? tooltipBlurb}</p>
        <p className="mt-1 text-muted-foreground">
          See the{' '}
          <Link href="/grading" className="underline">
            grading guide
          </Link>
          .
        </p>
      </TooltipContent>
    </Tooltip>
  );
}
